// src/pages/Admin/VotePage/components/VoteFormModal.jsx

import React from "react";

import {
  FaBalanceScale,
  FaBuilding,
  FaCalendarAlt,
  FaChartBar,
  FaExclamationTriangle,
  FaInfoCircle,
  FaLock,
  FaSave,
  FaToggleOn,
  FaVoteYea,
} from "react-icons/fa";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../../components/Modal/Modal";

import { ROLE_OPTIONS } from "../../../../utils/rolesOptions";

import useVoteForm from "../hooks/useVoteForm";
import AudienceSelector from "./AudienceSelector";

const cx = (...classes) =>
  classes.filter(Boolean).join(" ");

function ToggleCard({
  checked,
  onChange,
  icon: Icon,
  title,
  description,
  activeClasses,
  disabled = false,
}) {
  return (
    <label
      className={cx(
        "flex items-start gap-3 rounded-2xl border p-4 transition",
        disabled
          ? "cursor-not-allowed opacity-60"
          : "cursor-pointer",
        checked
          ? activeClasses
          : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only"
      />

      <span
        className={cx(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
          checked
            ? "bg-white/80 shadow-sm dark:bg-slate-950/50"
            : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
        )}
      >
        <Icon />
      </span>

      <span className="min-w-0">
        <span className="block text-sm font-black text-slate-900 dark:text-white">
          {title}
        </span>

        <span className="mt-1 block text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          {description}
        </span>
      </span>
    </label>
  );
}

export default function VoteFormModal({
  isOpen,
  onClose,
  vote,
  onSaved,
  actorGroups = [],
  actorGroupsLoading = false,
  authDebug,
  userContext,
}) {
  const {
    formData,
    roleIds,
    actorGroupIds,

    saving,
    isEditing,
    clubRulesLocked,

    handleFieldChange,
    setFieldValue,

    setOneVotePerClub,
    setClubWeightEnabled,

    toggleRole,
    toggleActorGroup,
    clearAudience,
    setRoleIds,

    submitForm,
  } = useVoteForm({
    isOpen,
    vote,
    onSaved,
    onClose,
    authDebug,
    userContext,
  });

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="vote-form-title"
      describedById="vote-form-description"
      initialFocusSelector="#vote-title-input"
    >
      <ModalHeader
        id="vote-form-title"
        title={
          isEditing
            ? "Modifier le vote ou sondage"
            : "Créer un vote ou sondage"
        }
        subtitle="Configurez les informations, la période, les règles et l’audience de la consultation."
        onClose={onClose}
      />

      <ModalBody id="vote-form-description">
        <form
          id="vote-admin-form"
          onSubmit={submitForm}
          className="space-y-5"
        >
          {/* Informations générales */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 sm:p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
                <FaVoteYea />
              </span>

              <div>
                <h3 className="font-black text-slate-900 dark:text-white">
                  Informations générales
                </h3>

                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Présentation visible par les utilisateurs.
                </p>
              </div>
            </div>

            <div>
              <label
                htmlFor="vote-title-input"
                className="text-sm font-black text-slate-700 dark:text-slate-200"
              >
                Titre
              </label>

              <input
                id="vote-title-input"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFieldChange}
                placeholder="Ex. Validation du projet sportif régional"
                required
                className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <div className="mt-4">
              <label
                htmlFor="vote-description-input"
                className="text-sm font-black text-slate-700 dark:text-slate-200"
              >
                Description
              </label>

              <textarea
                id="vote-description-input"
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleFieldChange}
                placeholder="Présentez le contexte et les objectifs de la consultation…"
                className="mt-1.5 w-full resize-y rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>
          </section>

          {/* Type et période */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 sm:p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
                <FaCalendarAlt />
              </span>

              <div>
                <h3 className="font-black text-slate-900 dark:text-white">
                  Type et période
                </h3>

                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Définissez le fonctionnement et les dates d’ouverture.
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() =>
                  setFieldValue(
                    "type",
                    "vote",
                  )
                }
                className={cx(
                  "flex items-start gap-3 rounded-2xl border p-4 text-left transition",
                  formData.type === "vote"
                    ? "border-indigo-300 bg-indigo-50 ring-2 ring-indigo-500/10 dark:border-indigo-500/40 dark:bg-indigo-500/10"
                    : "border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600",
                )}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
                  <FaVoteYea />
                </span>

                <span>
                  <span className="block text-sm font-black text-slate-900 dark:text-white">
                    Vote
                  </span>

                  <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                    Consultation décisionnelle ou validation formelle.
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() =>
                  setFieldValue(
                    "type",
                    "sondage",
                  )
                }
                className={cx(
                  "flex items-start gap-3 rounded-2xl border p-4 text-left transition",
                  formData.type === "sondage"
                    ? "border-violet-300 bg-violet-50 ring-2 ring-violet-500/10 dark:border-violet-500/40 dark:bg-violet-500/10"
                    : "border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600",
                )}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                  <FaChartBar />
                </span>

                <span>
                  <span className="block text-sm font-black text-slate-900 dark:text-white">
                    Sondage
                  </span>

                  <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                    Recueil d’opinion ou consultation informative.
                  </span>
                </span>
              </button>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-black text-slate-700 dark:text-slate-200">
                  Date de début
                </label>

                <input
                  type="date"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleFieldChange}
                  required
                  className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <label className="text-sm font-black text-slate-700 dark:text-slate-200">
                  Date de fin
                </label>

                <input
                  type="date"
                  name="end_time"
                  value={formData.end_time}
                  min={formData.start_time || undefined}
                  onChange={handleFieldChange}
                  required
                  className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <ToggleCard
                checked={formData.is_active}
                onChange={(event) =>
                  setFieldValue(
                    "is_active",
                    event.target.checked,
                  )
                }
                icon={FaToggleOn}
                title="Consultation active"
                description="La consultation sera accessible durant la période définie."
                activeClasses="border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10"
              />

              <ToggleCard
                checked={formData.is_anonymous}
                onChange={(event) =>
                  setFieldValue(
                    "is_anonymous",
                    event.target.checked,
                  )
                }
                icon={FaLock}
                title="Réponses anonymes"
                description="Les résultats ne permettront pas d’identifier le choix de chaque votant."
                activeClasses="border-blue-300 bg-blue-50 dark:border-blue-500/40 dark:bg-blue-500/10"
              />
            </div>
          </section>

          {/* Règles des clubs */}
          <section className="overflow-hidden rounded-2xl border border-amber-200 bg-white dark:border-amber-500/30 dark:bg-slate-900">
            <div className="border-b border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 dark:border-amber-500/20 dark:from-amber-500/10 dark:to-orange-500/10 sm:p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
                  <FaBuilding />
                </span>

                <div>
                  <h3 className="font-black text-slate-900 dark:text-white">
                    Représentation des clubs
                  </h3>

                  <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                    Activez ces règles lorsque les participants votent
                    au nom de leur club et non uniquement à titre individuel.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-5">
              {clubRulesLocked ? (
                <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />

                  <div>
                    <p className="text-sm font-black">
                      Règles verrouillées
                    </p>

                    <p className="mt-1 text-xs leading-relaxed">
                      Des participants ont déjà voté. Les règles de
                      représentation des clubs ne peuvent plus être modifiées.
                    </p>
                  </div>
                </div>
              ) : null}

              <div className="grid gap-3 lg:grid-cols-2">
                <ToggleCard
                  checked={formData.one_vote_per_club}
                  onChange={(event) =>
                    setOneVotePerClub(
                      event.target.checked,
                    )
                  }
                  disabled={
                    clubRulesLocked ||
                    formData.use_club_weight
                  }
                  icon={FaBuilding}
                  title="Vote unique par club"
                  description="Dès qu’un responsable valide le bulletin, les autres responsables du même club ne peuvent plus voter."
                  activeClasses="border-indigo-300 bg-indigo-50 dark:border-indigo-500/40 dark:bg-indigo-500/10"
                />

                <ToggleCard
                  checked={formData.use_club_weight}
                  onChange={(event) =>
                    setClubWeightEnabled(
                      event.target.checked,
                    )
                  }
                  disabled={clubRulesLocked}
                  icon={FaBalanceScale}
                  title="Pondération selon les licenciés"
                  description="Le poids du club est calculé par tranche de 50 licenciés. Cette option active automatiquement le vote unique par club."
                  activeClasses="border-orange-300 bg-orange-50 dark:border-orange-500/40 dark:bg-orange-500/10"
                />
              </div>

              {formData.use_club_weight ? (
                <div className="mt-4 rounded-2xl border border-orange-200 bg-orange-50/80 p-4 dark:border-orange-500/20 dark:bg-orange-500/10">
                  <div className="flex items-start gap-3">
                    <FaBalanceScale className="mt-0.5 shrink-0 text-orange-600 dark:text-orange-300" />

                    <div>
                      <p className="text-sm font-black text-orange-900 dark:text-orange-100">
                        Barème appliqué
                      </p>

                      <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold">
                        <span className="rounded-full bg-white px-3 py-1.5 text-orange-800 ring-1 ring-orange-200 dark:bg-slate-900 dark:text-orange-200 dark:ring-orange-500/20">
                          1–50 : poids 1
                        </span>

                        <span className="rounded-full bg-white px-3 py-1.5 text-orange-800 ring-1 ring-orange-200 dark:bg-slate-900 dark:text-orange-200 dark:ring-orange-500/20">
                          51–100 : poids 2
                        </span>

                        <span className="rounded-full bg-white px-3 py-1.5 text-orange-800 ring-1 ring-orange-200 dark:bg-slate-900 dark:text-orange-200 dark:ring-orange-500/20">
                          101–150 : poids 3
                        </span>

                        <span className="rounded-full bg-white px-3 py-1.5 text-orange-800 ring-1 ring-orange-200 dark:bg-slate-900 dark:text-orange-200 dark:ring-orange-500/20">
                          Puis +1 par tranche de 50
                        </span>
                      </div>

                      <p className="mt-3 text-xs leading-relaxed text-orange-800 dark:text-orange-200">
                        Un club ayant 0 licencié déclaré ne pourra pas
                        participer à ce vote.
                      </p>
                    </div>
                  </div>
                </div>
              ) : formData.one_vote_per_club ? (
                <div className="mt-4 flex items-start gap-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-200">
                  <FaInfoCircle className="mt-0.5 shrink-0" />

                  <p className="text-xs leading-relaxed">
                    Tous les clubs autorisés disposeront d’une seule voix,
                    sans tenir compte de leur nombre de licenciés.
                  </p>
                </div>
              ) : (
                <div className="mt-4 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                  <FaInfoCircle className="mt-0.5 shrink-0" />

                  <p className="text-xs leading-relaxed">
                    Sans option activée, chaque utilisateur autorisé peut
                    participer individuellement avec un poids de 1.
                  </p>
                </div>
              )}
            </div>
          </section>

          <AudienceSelector
            roleOptions={ROLE_OPTIONS}
            selectedRoleIds={roleIds}
            selectedActorGroupIds={actorGroupIds}
            actorGroups={actorGroups}
            actorGroupsLoading={actorGroupsLoading}
            onToggleRole={toggleRole}
            onToggleActorGroup={toggleActorGroup}
            onSelectAllRoles={setRoleIds}
            onClearAudience={clearAudience}
          />
        </form>
      </ModalBody>

      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Annuler
        </button>

        <button
          type="submit"
          form="vote-admin-form"
          disabled={saving}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 text-sm font-black text-white shadow-lg shadow-indigo-900/15 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FaSave
            className={
              saving
                ? "animate-pulse"
                : ""
            }
          />

          {saving
            ? "Enregistrement…"
            : isEditing
              ? "Enregistrer les modifications"
              : "Créer la consultation"}
        </button>
      </ModalFooter>
    </Modal>
  );
}